import { IconFileAttachmentOutlined } from '@repo/monochrome-icons';
import cx from 'clsx';
import type { ComponentType, ReactElement, SVGProps } from 'react';
import { useState } from 'react';
import { Button } from 'react-aria-components';
import { useIntl } from 'react-intl';
import styles from '../attachment.module.css';

export interface AttachmentIconProps {
  className?: string;
  fileUrl?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  onPreview?: () => void;
  thumbnail?: string;
  thumbnailAlt?: string;
}

export function AttachmentIcon({
  className,
  fileUrl,
  icon: Icon = IconFileAttachmentOutlined,
  onPreview,
  thumbnail,
  thumbnailAlt = '',
}: AttachmentIconProps): ReactElement {
  const { formatMessage } = useIntl();
  const [imageError, setImageError] = useState(false);

  const icon = (
    <span className={cx(styles.iconContainer, thumbnail && styles.iconThumbnail, className)}>
      {thumbnail && !imageError ? (
        <img
          alt={thumbnailAlt}
          className={styles.iconImage}
          onError={() => {
            setImageError(true);
          }}
          src={thumbnail}
        />
      ) : (
        <Icon />
      )}
    </span>
  );

  if (onPreview) {
    return (
      <Button
        aria-label={formatMessage({ id: 'a11y-buttons-open-file-aria-label' })}
        className={styles.previewButton}
        data-test-preview-button-link
        onPress={onPreview}
        type="button"
      >
        {icon}
      </Button>
    );
  }

  if (fileUrl) {
    return (
      <a
        aria-label={formatMessage({ id: 'a11y-buttons-open-file-aria-label' })}
        href={fileUrl}
        rel="noreferrer"
        target="_blank"
      >
        {icon}
      </a>
    );
  }

  return icon;
}
